import {
  BASE_SETTING_PRIORITIES,
  DEVIATION_SETTING_PRIORITIES,
  FOLLOW_UP_SETTING_PRIO,
  SETTLEMENTS_SETTING_PRIO,
} from 'api/digitalTwin/digitalTwinConstants'
import { OptJobType } from 'api/optJobs/optJobs.api'
import i18n from 'i18next'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import { COLLECTED_SETTINGS_FILTER_CONFIG } from './collectedSettingsFilterConfig'

export type CollectedSettingsFilterMode = 'deviation' | 'optimization'

export type OptimizationSubtypeOption = {
  id: string
  label: string
  optJobType: OptJobType
  priorityLevels: number[]
  subtype?: string
  parentLabel?: string
  /**
   * Set on an opt job type the list also offers per subtype. Its settings are the union of its subtypes'
   * and no single run has them, so it has no applied values to show.
   */
  isSplitIntoSubtypes?: boolean
}

function isOptJobTypeAllowedByConfig(optJobType: OptJobType): boolean {
  const { allowedOptJobTypes } = COLLECTED_SETTINGS_FILTER_CONFIG

  if (allowedOptJobTypes.length > 0 && !allowedOptJobTypes.includes(optJobType)) {
    return false
  }

  return true
}

function shouldExpandSubtypesForOptJobType(optJobType: OptJobType): boolean {
  return COLLECTED_SETTINGS_FILTER_CONFIG.subtypeExpandedOptJobTypes.includes(optJobType)
}

export function getPriorityLevelDisplayNames(
  priorityLevel: number | number[],
  systemPriorityLevels: SystemSettingPriorityLevel[] | null | undefined
): string {
  const priorityLevels = Array.isArray(priorityLevel) ? priorityLevel : [priorityLevel]

  const allPriorityLevels = [
    ...BASE_SETTING_PRIORITIES,
    ...DEVIATION_SETTING_PRIORITIES,
    ...[FOLLOW_UP_SETTING_PRIO],
    ...[SETTLEMENTS_SETTING_PRIO],
    ...(systemPriorityLevels ?? []),
  ]

  return priorityLevels
    .map((level) =>
      i18n.t(allPriorityLevels.find((priority) => priority.priority_level === level)?.display_name ?? String(level))
    )
    .join(', ')
}

export function getHiddenPriorityLevelSet(
  systemPriorityLevels: SystemSettingPriorityLevel[] | null | undefined
): Set<number> {
  return new Set(
    (systemPriorityLevels ?? []).filter((level) => level.is_hidden === true).map((level) => level.priority_level)
  )
}

export function getConfigurablePriorityLevels(subtypes: Subtype[], hiddenPriorityLevels: Set<number>): number[] {
  return [...new Set(subtypes.flatMap((subtype) => subtype.configurable_priority_levels ?? []))]
    .filter((level) => !hiddenPriorityLevels.has(level))
    .sort((a, b) => a - b)
}

function getSubtypeOption(
  subtype: Subtype,
  optJobTypeOption: OptimizationSubtypeOption,
  hiddenPriorityLevels: Set<number>
): OptimizationSubtypeOption {
  return {
    id: `${optJobTypeOption.optJobType}:${subtype.subtype}`,
    label: subtype.subtype_display_name || subtype.subtype,
    optJobType: optJobTypeOption.optJobType,
    priorityLevels: getConfigurablePriorityLevels([subtype], hiddenPriorityLevels),
    subtype: subtype.subtype,
    parentLabel: optJobTypeOption.label,
  }
}

function getOptionsForOptJobType(
  optJobType: OptJobType,
  subtypes: Subtype[],
  hiddenPriorityLevels: Set<number>,
  translations?: { [key: string]: string }
): OptimizationSubtypeOption[] {
  const optJobTypeOption: OptimizationSubtypeOption = {
    id: optJobType,
    label: getPrettyName(optJobType, translations),
    optJobType,
    priorityLevels: getConfigurablePriorityLevels(subtypes, hiddenPriorityLevels),
  }

  if (optJobTypeOption.priorityLevels.length === 0) {
    return []
  }

  if (!shouldExpandSubtypesForOptJobType(optJobType)) {
    return [optJobTypeOption]
  }

  const subtypeOptions = subtypes
    .map((subtype) => getSubtypeOption(subtype, optJobTypeOption, hiddenPriorityLevels))
    .filter((subtypeOption) => subtypeOption.priorityLevels.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label))

  return [{ ...optJobTypeOption, isSplitIntoSubtypes: true }, ...subtypeOptions]
}

export function getOptimizationTypeOptions(
  subtypesByOptJobType: Partial<Record<OptJobType, Subtype[]>> | undefined,
  systemPriorityLevels?: SystemSettingPriorityLevel[] | null,
  translations?: { [key: string]: string }
): OptimizationSubtypeOption[] {
  if (!subtypesByOptJobType) {
    return []
  }

  const hiddenPriorityLevels = getHiddenPriorityLevelSet(systemPriorityLevels)

  return Object.entries(subtypesByOptJobType)
    .filter(([optJobType]) => isOptJobTypeAllowedByConfig(optJobType as OptJobType))
    .map(([optJobType, subtypes]) =>
      getOptionsForOptJobType(optJobType as OptJobType, subtypes ?? [], hiddenPriorityLevels, translations)
    )
    .filter((options) => options.length > 0)
    .sort(([a], [b]) => a.label.localeCompare(b.label))
    .flat()
}

export function getOptimizationTypeOptionDisplayLabel(option: OptimizationSubtypeOption): string {
  if (!option.parentLabel) {
    return option.label
  }

  return `${option.parentLabel} – ${option.label}`
}

export function getPrioritiesFromPriorityLevels(
  selectedPriorityLevels: number[],
  availablePriorities: SystemSettingPriorityLevel[]
): SystemSettingPriorityLevel[] {
  return selectedPriorityLevels
    .map((priorityLevel) => {
      const matchingPriority = availablePriorities.find((priority) => priority.priority_level === priorityLevel)
      if (matchingPriority) {
        return matchingPriority
      }

      return {
        priority_level: priorityLevel,
        display_name: String(priorityLevel),
      }
    })
    .sort((a, b) => a.priority_level - b.priority_level)
}

export function filterOutHiddenPriorityLevels(
  priorityLevels: number[],
  systemPriorityLevels: SystemSettingPriorityLevel[] | null | undefined
): number[] {
  const hiddenPriorityLevels = getHiddenPriorityLevelSet(systemPriorityLevels)
  return priorityLevels.filter((level) => !hiddenPriorityLevels.has(level))
}

export function getCollectedSettingsFormPriorities(
  priorities: SystemSettingPriorityLevel[]
): SystemSettingPriorityLevel[] {
  return priorities.filter((priority) => priority.is_hidden !== true)
}
